import { useEffect, useRef, useState } from "react";
import { Badge, Modal, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import { AdminButton } from "../../SuperAdmin/SharedComponents/ListView";
import { contractApi } from "../API/ContractAPI";
import SafeHtml from "./SafeHtml";

// dd.mm.yyyy hh:mm in the viewer's local time (the API returns UTC).
const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/** Who accepted the current version of a contract, who hasn't, and every past acceptance. */
export default function ContractLogModal({ show, contractId, token, onHide }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: false, log: null, error: null });
  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: state.log ? `${state.log.name} - v${state.log.versionNumber}` : "contract-log",
  });

  useEffect(() => {
    if (!show || !contractId) return undefined;
    // A late answer for a contract opened earlier must not replace this one (and get printed).
    let current = true;
    setState({ loading: true, log: null, error: null });
    contractApi.log(token, contractId).then((r) => {
      if (!current) return;
      setState({ loading: false, log: r.ok ? r.data : null, error: r.ok ? null : r.message || t("contractLoadFailed") });
    });
    return () => {
      current = false;
    };
  }, [show, contractId, token, t]);

  const { loading, log, error } = state;

  const statusBadge = (person) => {
    if (person.accepted) return <Badge bg="success">{t("contractStatusAccepted")}</Badge>;
    if (!person.hasAccount) return <Badge bg="light" text="dark">{t("contractStatusNoAccount")}</Badge>;
    return <Badge bg="secondary">{t("contractStatusPending")}</Badge>;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{t("contractLog")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {error && <div className="text-danger">{error}</div>}

        {log && (
          <div ref={printRef} className="contract-log">
            <div className="contract-log__head">
              <h5 className="mb-0">{log.name}</h5>
              <Badge bg="primary">
                {t("contractVersion")} {log.versionNumber}
              </Badge>
              {log.isDeleted && <Badge bg="danger">{t("contractDeletedBadge")}</Badge>}
            </div>

            <div className="table-responsive">
              <Table size="sm" className="contract-log__table">
                <thead>
                  <tr>
                    <th>{t("contractPerson")}</th>
                    <th>{t("contractEmail")}</th>
                    <th>{t("contractStatus")}</th>
                    <th>{t("contractDate")}</th>
                  </tr>
                </thead>
                <tbody>
                  {log.people.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-muted">
                        {t("contractNoPeople")}
                      </td>
                    </tr>
                  )}
                  {log.people.map((p, i) => (
                    <tr key={`${p.email}-${i}`}>
                      <td>{p.name}</td>
                      <td>{p.email}</td>
                      <td>{statusBadge(p)}</td>
                      <td className="tabular-nums">{formatDate(p.acceptedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <h6 className="contract-log__subhead">{t("contractHistory")}</h6>
            <div className="table-responsive">
              <Table size="sm" className="contract-log__table">
                <thead>
                  <tr>
                    <th>{t("contractVersion")}</th>
                    <th>{t("contractPerson")}</th>
                    <th>{t("contractEmail")}</th>
                    <th>{t("contractDate")}</th>
                  </tr>
                </thead>
                <tbody>
                  {log.history.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-muted">
                        {t("contractNoHistory")}
                      </td>
                    </tr>
                  )}
                  {log.history.map((h, i) => (
                    <tr key={i}>
                      <td>v{h.versionNumber}</td>
                      <td>{h.acceptedByName}</td>
                      <td>{h.acceptedByEmail}</td>
                      <td className="tabular-nums">{formatDate(h.acceptedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <h6 className="contract-log__subhead">{t("contractCurrentText")}</h6>
            <SafeHtml className="contract-text" html={log.content} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <AdminButton variant="outline" onClick={handlePrint} disabled={!log}>
          {t("contractPrint")}
        </AdminButton>
        <AdminButton onClick={onHide}>{t("contractClose")}</AdminButton>
      </Modal.Footer>
    </Modal>
  );
}
